import type { NextCorporateAction, StockShortKeyData } from "@/models/stock";
import { isUsExchange } from "./vars";

/**
 * Titles, H1s and the server-rendered first lines of the stock pages (`/s/[exchange]/[ticker]/*`).
 *
 * One pattern on every tab, "Company (TICKER) Topic", the way people search ("GameStop short
 * interest", "AAPL dividend") and the way the pages that rank for those searches are titled. The
 * titles used to lead with "Company, EXCHANGE:TICKER | Tab | ORTEX" (up to 98 characters), so the
 * part a search result shows was the name and the exchange and the topic was cut off.
 *
 * No React here: the unit tests import it directly (`e2e/unit/stockSeo.spec.ts`).
 */

/** Longest title most search results show without cutting it. */
export const TITLE_MAX = 60;
const SUFFIX = " | ORTEX";

/** What the builders need from a stock: the Stock model and the free-stock refs both fit. */
export interface StockSeoRef {
    name?: string | null;
    ticker: string;
    exchangesymbol: string;
    shortKeyData?: StockShortKeyData | null;
    next_corporate_action?: NextCorporateAction | null;
}

/**
 * The first candidate that fits in {@link TITLE_MAX}, each tried with " | ORTEX" first and then without:
 * the brand goes before any of the title's meaning does. Candidates run from the fullest to the
 * shortest (the ticker alone), so a long company name falls back instead of being cut mid-word.
 */
export const fitTitle = (...candidates: string[]): string => {
    for (const candidate of candidates) {
        if (candidate.length + SUFFIX.length <= TITLE_MAX) return candidate + SUFFIX;
        if (candidate.length <= TITLE_MAX) return candidate;
    }
    return candidates[candidates.length - 1];
};

/** "GameStop Corp. (GME)", or the ticker alone when the stock has no name. */
export const companyLabel = (stock: StockSeoRef): string => (stock.name ? `${stock.name} (${stock.ticker})` : stock.ticker);

/**
 * The tab a stock-page pathname shows: "" for the overview, "short-interest", "corporate-actions/all", ...
 * Accepts both the `/s/[exchange]/[ticker]` and the `/id/[stock_id]` route patterns.
 */
export const stockTabOf = (pathname: string): string => {
    const match = /^\/(?:s\/\[exchange\]\/\[ticker\]|id\/\[stock_id\])(?:\/(.*))?$/.exec(pathname);
    return match?.[1] ?? "";
};

/** The H1 topic per tab, after "Company (TICKER)". A tab not listed gets the bare company label. */
const HEADING_TOPICS: Record<string, string> = {
    "short-interest": "Short Interest",
    dividend: "Dividend",
    options: "Options",
    "options-platform": "Options",
    analysts: "Analyst Ratings",
    insiders: "Insider Trading",
    holders: "Institutional Ownership",
    fundamentals: "Financials",
    news: "News",
    events: "Events",
    eps: "EPS Momentum",
    peers: "Peers",
    "commodity-exposure": "Commodity Exposure",
    "trading-signals": "Trading Signals",
    "stock-scores": "Stock Scores",
    "corporate-actions": "Corporate Actions",
    "corporate-actions/all": "Corporate Actions",
    "corporate-actions/stock-splits": "Stock Splits",
    "alt-data": "Alternative Data",
    bonds: "Bonds",
    "activist-investors": "Activist Investors",
    "index-rebalance": "Index Rebalance",
    "trading-idea": "Trading Idea",
    "stock-report": "Stock Report",
};

/**
 * The page's one H1: "GameStop Corp. (GME)" on the overview, "GameStop Corp. (GME) Short Interest" on
 * the short-interest tab, and so on. `tab` is {@link stockTabOf}'s answer.
 */
export const stockHeading = (stock: StockSeoRef, tab: string): string => {
    const label = companyLabel(stock);
    const topic = HEADING_TOPICS[tab] ?? HEADING_TOPICS[tab.split("/")[0]];
    return topic ? `${label} ${topic}` : label;
};

/** Each tab's title candidates, fullest first; the last one never names the company. */
const titleCandidates = (stock: StockSeoRef, tab: string): string[] | null => {
    const label = companyLabel(stock);
    const t = stock.ticker;
    switch (tab) {
        case "": {
            const second = isUsExchange(stock.exchangesymbol) ? "Options" : "Estimates";
            return [`${label} Stock: Short Interest, ${second} & Insiders`, `${label} Stock: Short Interest & ${second}`, `${t} Stock: Short Interest, ${second} & Insiders`, `${t} Stock`];
        }
        case "short-interest":
            return [...(stock.name ? [`${t} Short Interest & Cost to Borrow: ${stock.name}`] : []), `${t} Short Interest & Cost to Borrow`];
        case "dividend":
            return [`${label} Dividend: Next Ex-Date, Forecast & History`, `${label} Dividend: Next Ex-Date & History`, `${t} Dividend: Next Ex-Date, Forecast & History`];
        case "options":
            return [`${label} Options Chain, Flow & Unusual Activity`, `${label} Options Chain & Flow`, `${t} Options Chain, Flow & Unusual Activity`];
        case "analysts":
            return [`${label} Analyst Ratings & Price Targets`, `${label} Analyst Ratings`, `${t} Analyst Ratings & Price Targets`];
        case "insiders":
            return [`${label} Insider Trading: Buys & Sells`, `${label} Insider Trading`, `${t} Insider Trading: Buys & Sells`];
        case "holders":
            return [`${label} Institutional Ownership`, `${t} Institutional Ownership`];
        case "fundamentals":
            return [`${label} Financials`, `${t} Financials`];
        case "news":
            return [`${label} Stock News`, `${t} Stock News`];
        default:
            return null;
    }
};

/**
 * The `<title>` (and og:title / twitter:title) of a stock tab, at most {@link TITLE_MAX} characters.
 * Only the tabs that have one here: the rest keep their own.
 */
export const stockTitle = (stock: StockSeoRef, tab: "" | "short-interest" | "dividend" | "options" | "analysts" | "insiders" | "holders" | "fundamentals" | "news"): string => fitTitle(...(titleCandidates(stock, tab) ?? [stock.ticker]));

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "2026-10-29" as "Oct 29, 2026". By hand rather than `toLocaleDateString`: the server's and the
 * browser's ICU data differ, and the text must be the same on both or the page re-renders on hydration.
 * Anything that is not an ISO date gives null.
 */
export const usDate = (iso: string | null | undefined): string | null => {
    const match = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
    if (!match) return null;
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${MONTHS[month - 1]} ${day}, ${match[1]}`;
};

const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const num = (value: number, digits: number) => value.toLocaleString("en-US", { maximumFractionDigits: digits });

/** What the crawlable sentence ends with for a visitor: the live figures are a subscriber's. */
export const LIVE_DATA_CALL = "Sign up for live short interest data.";
/** The same line for a signed-in reader below Advanced. */
export const UPGRADE_DATA_CALL = "Upgrade to ORTEX Advanced for live short interest data.";

/** Who is reading: decides the tense and the closing line of the short-interest sentence. */
export type ShortReader = "visitor" | "subscriber" | "advanced";

/** The reader kind for a user payload: a visitor while unknown, with no plan, or on the visitor plan. */
export const shortReaderOf = (user: { alt_data?: boolean; subscription_plan?: string } | null | undefined): ShortReader =>
    !user || !user.subscription_plan || user.subscription_plan === "ORTEX Visitor" ? "visitor" : user.alt_data ? "advanced" : "subscriber";

/**
 * The short-interest tab's first line, from the numbers on the stock payload. TED serves them two
 * weeks behind the latest estimate to any reader below Advanced (ted!4215; Peter, 2026-11-02: the
 * live figures gave away too much), so the default wording is the delayed one:
 * "As of Oct 16, 2026, ORTEX estimated 9% of free float sold short, 4.1 days to cover, cost to borrow
 * 0.51%. Sign up for live short interest data." A signed-in reader below Advanced gets the upgrade
 * line instead; an Advanced reader gets the present tense and no closing line. Empty when the payload
 * has none of the numbers.
 */
export const shortAsOfSentence = (data: StockShortKeyData | null | undefined, reader: ShortReader = "visitor"): string => {
    const live = reader === "advanced";
    if (!data) return "";
    const rest: string[] = [];
    if (finite(data.daysToCover)) rest.push(`${num(data.daysToCover, 1)} days to cover`);
    if (finite(data.costToBorrow)) rest.push(`cost to borrow ${num(data.costToBorrow, 2)}%`);
    const lead = finite(data.siPercentFreeFloat) ? `ORTEX ${live ? "estimates" : "estimated"} ${num(data.siPercentFreeFloat, 1)}% of free float ${live ? "is " : ""}sold short` : null;
    if (!lead && !rest.length) return "";
    const date = usDate(data.asOf);
    const body = lead ? [lead, ...rest].join(", ") : `ORTEX ${live ? "shows" : "showed"} ${rest.join(", ")}`;
    return `${date ? `As of ${date}, ` : ""}${body}.${live ? "" : ` ${reader === "subscriber" ? UPGRADE_DATA_CALL : LIVE_DATA_CALL}`}`;
};

/**
 * The dividend tab's first line, from the stock payload's next corporate action:
 * "Next ex-dividend date: Nov 10, 2026 (0.26 USD)." Empty when the next action is not a dividend.
 * The ORTEX forecast and its range are left out: they load in the browser and are a subscriber's.
 */
export const nextDividendLine = (action: NextCorporateAction | null | undefined): string => {
    if (!action || action.type !== "dividend") return "";
    const date = usDate(action.date);
    if (!date) return "";
    return `Next ex-dividend date: ${date}${action.detail ? ` (${action.detail})` : ""}.`;
};

/** Meta descriptions that match the titles above. */
export const stockDescriptions = {
    dividend: (stock: StockSeoRef): string => `${companyLabel(stock)} dividend: the next ex-dividend date, ORTEX forecast amount and range, chance of a raise or cut, yield and the full dividend history.`,
    options: (stock: StockSeoRef): string => `${companyLabel(stock)} options: the options chain, live options flow, unusual activity, put/call ratio and implied volatility, from ORTEX.`,
    analysts: (stock: StockSeoRef): string => `${companyLabel(stock)} analyst ratings and price targets: consensus recommendation, target price, estimate revisions and the latest upgrades and downgrades.`,
    holders: (stock: StockSeoRef): string => `${companyLabel(stock)} institutional ownership: the largest institutional holders, their positions and how they changed, from regulatory filings.`,
    fundamentals: (stock: StockSeoRef): string => `${companyLabel(stock)} financials: income statement, balance sheet, cash flow, valuation multiples and ratios.`,
    news: (stock: StockSeoRef): string => `${companyLabel(stock)} stock news: the latest headlines and company announcements.`,
};
